import { CommonAreaRule, Service, BathAreaRule } from '..'
import { StripeConnectAccount } from '../Rent'

import { Address, IdentifiableNamedObject, IdentifiableObject } from '../types'
import { User } from '../Users'

/** Application from global list (e.g. GET /company/applications/). */
export interface Application extends IdentifiableObject {
    name: string
    slug: string
    description: string
    is_active: boolean
    /** Default price as decimal string (e.g. "0.00", "99.00"). */
    default_price: string
    /** Short summary; may be omitted by API. */
    short_description?: string
}

/** Application as returned on a company/workspace (includes pricing for that company). */
export interface CompanyApplication extends Application {
    /** Price the company pays for this app (custom_price ?? default_price). Use for display/billing. */
    effective_price: string
    /** Company-specific price; null means use default_price. Use for "custom price set" or edit UIs. */
    custom_price: string | null
}

export interface Company extends IdentifiableNamedObject {
    //this is the response from the company list serializer
    address: string
    city: string
    state: string
    zip_code: string
    phone: string | null
    company_type: CompanyType
    turn_co_sheet_vendor: number | null
    email?: string
    employees?: User[]
    organization: number
    organization_name: string
    organization_entrata_enabled?: boolean
    entrata_integrated: boolean
    move_out_inspection_config?: number | null
    property_manager?: User | null
    profile_picture: string | null
    url: string | null
    inv_sheet_uploaded: boolean
    area_sheet_uploaded: boolean
    infrastructure_sheet_uploaded: boolean
    status_group_sheet_uploaded: boolean
    unit_sheet_uploaded: boolean
    allow_service_request: boolean | null
    stripe_subscription: StripeSubscription | null
    has_infrastructure: boolean
    account_manager_channel: number | null
    register_step?: RegisterStep
    skillcat?: boolean
    run_auto_move_out_events: boolean
    run_inspection_frequencies: boolean
    run_action_item_frequencies: boolean
    days_before_move_out_schedule: number | null
    company_hours?: CompanyHours | null
    on_call_backups?: OnCallBackups | null
    billable_unit_count: number
    billable_unit_rate: FloatString
    primary_address?: Address | null
    common_area_rule_type?: CommonAreaRule | null
    bath_area_rule_type?: BathAreaRule | null
    billing_group_display_name?: string | null
    billing_group?: number | null
    lease_sync_schedule?: LeaseSyncSchedule | null
    auto_move_in_enabled?: boolean | null
    auto_move_in_days_to_complete?: number | null
    send_date_offset?: number | null
    stripe_connect_account?: StripeConnectAccount | null
    tenant_bill_generation_day?: number | null
    tenant_bill_send_day_offset?: number | null
    tenant_bill_due_day_offset?: number | null
    tenant_synced_damage_view?: boolean | null
    tenant_approved_damage_view?: boolean | null
    lease_damage_end_date_cutoff?: number | null
    status: CompanyStatus
    /** Present on admin associated-apartments responses. */
    apartment_vendor_status?: ApartmentVendorRelationshipStatus
    /** Applications granted to this company; includes effective_price and custom_price per app. */
    applications?: CompanyApplication[]
    default_move_out_rule?: number | null
    has_integration_error: boolean
    is_appfolio_property: boolean
    schedule_auto_archive_days?: number | null
    ai_project_reports_enabled?: boolean | null
}

export interface ServedSchoolRow {
    school_id: number
    effective_display_name: string
    city: string | null
    state: string | null
    is_served: boolean
    is_primary: boolean
    source: 'inferred' | 'manual'
    inferred_distance_miles: number | null
    fall_enrollment_total?: number | null
}

export interface ServedSchoolsResponse {
    inference_radius_miles: number
    default_served_radius_miles: number
    schools: ServedSchoolRow[]
}

export interface ServedSchoolPatchItem {
    school_id: number
    is_served?: boolean
    is_primary?: boolean
}

export type CommunityFurnished = 'unknown' | 'yes' | 'no' | 'partial'
export type CommunityLeaseStyle = 'unknown' | 'individual' | 'joint'

export interface CommunityFee {
    name: string
    amount: string
    covers: string
}

export interface CommunityFaq {
    question: string
    answer: string
    enabled: boolean
    source_url: string
}

export interface CommunityPetDefaults {
    allowed_animal_types: string[]
    max_pets_per_unit: string
    notes: string
}

export type CommunityWeekday =
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday'

export interface CommunityDayHours {
    closed: boolean
    start: string
    end: string
}

export type CommunityOfficeHours = Record<CommunityWeekday, CommunityDayHours>

export interface CommunityProfile {
    website_url?: string
    website_filled_at?: string | null
    website_fill_url?: string
    amenities: string
    parking: string
    laundry: string
    utilities_included: string
    income_requirements: string
    security_deposit: string
    pet_notes: string
    insurance_notes: string
    furnished: CommunityFurnished
    lease_style: CommunityLeaseStyle
    roommate_matching: boolean | null
    students_required: boolean | null
    extra_fees: CommunityFee[]
    faqs: CommunityFaq[]
    pet_defaults: CommunityPetDefaults
    office_hours: CommunityOfficeHours
    profile_picture?: string
    logo_url?: string
}

export type WebsiteExtractStatus = 'queued' | 'running' | 'ready' | 'failed'

export interface WebsiteExtractJob {
    id: number
    status: WebsiteExtractStatus
    start_url: string
    pages: { url: string; title: string }[]
    result: Partial<CommunityProfile>
    error: string
    logo_url?: string
    profile_picture?: string
    website_filled_at?: string | null
    created_date: string
    modified_date: string
}

export interface ListVendor extends BaseVendor {
    // the list serializer with all the things that are unique to the detail serializer listed as optional
    services: Service[]
    in_house?: number | null
    my_team?: boolean
    employees?: User[]
    wo_count?: number
    sch_count?: number
    tagline?: string
    url?: string
    sponsor_image?: string
    will_travel?: boolean
    plan: string | null
    step?: string
    imported?: boolean
    featured_image?: string
    description?: string
    documents?: VendorDocuments
    tokens?: number
    entrata_sync_enabled?: boolean
    profile_picture?: string
    register_step?: RegisterStep
    is_skillcat: boolean
    company_hours?: CompanyHours | null
    /** National rating; 0 = no rating. */
    rating?: number
    rating_updated_at?: string | null
}

export interface BaseVendor extends IdentifiableNamedObject {
    address: string
    city: string
    state: string
    zip_code: string
    phone: string | null
    email?: string
}

export interface RatingInterface {
    professionalism: number
    work_quality: number
}

export interface VendorScheduleRating
    extends IdentifiableObject,
        RatingInterface {
    vendor: number
    schedule: number
}

export interface VendorAd extends IdentifiableObject {
    vendor: number
    ad: string
}

export interface VendorDocuments {
    reference_1?: Document
    reference_2?: Document
    reference_3?: Document
    insurance_1?: Document
    insurance_2?: Document
    w9_document?: Document
}

export interface Document {
    name?: string
    url?: string
}

export enum PlanStatus {
    NO_PLAN = 'NO_PLAN',
    EZVENDOR = 'EZVENDOR',
    PREMIUM = 'PREMIUM',
}

export interface StripeSubscription {
    plan: PlanStatus
    stripe_customer_id: string
}

export interface SimpleVendor extends IdentifiableNamedObject {
    primary_address: Address
    is_trained: boolean
    will_travel: boolean
    profile_picture: string
    is_skillcat: boolean
    complete_count?: number
    description?: string
    phone?: string
}

export interface BaseOrganization {
    id: number
    name: string
    entrata_enabled?: boolean
}

export interface Organization extends IdentifiableNamedObject {
    entrata_username: string
    entrata_password: string
    entrata_endpoint: string
    slug: string | null
    entrata_ar_transaction_code: number | null
    archived: boolean
    property_count: number | null
    entrata_enabled?: boolean
}

export enum RegisterStep {
    DETAIL_STEP = 0,
    SERVICE_AREA_STEP = 1,
    SELECT_SERVICES_STEP = 2,
    AGREEMENTS_STEP = 3,
    COMPLETED_STEP = 4,
}

export interface BaseCompany {
    id: number
    name: string
    company_type: CompanyType
    primary_address: Address | null
    organization: IdentifiableNamedObject
    billable_unit_count: number
    billable_unit_rate: FloatString
}

export interface CompanyHours extends IdentifiableObject {
    monday_start: string | null
    monday_end: string | null
    monday_closed: boolean
    tuesday_start: string | null
    tuesday_end: string | null
    tuesday_closed: boolean
    wednesday_start: string | null
    wednesday_end: string | null
    wednesday_closed: boolean
    thursday_start: string | null
    thursday_end: string | null
    thursday_closed: boolean
    friday_start: string | null
    friday_end: string | null
    friday_closed: boolean
    saturday_start: string | null
    saturday_end: string | null
    saturday_closed: boolean
    sunday_start: string | null
    sunday_end: string | null
    sunday_closed: boolean
}

export interface ApartmentVendorRule extends IdentifiableObject {
    apartment: number
    service: number
    vendor: number
    rank: number
    buffer_days: number
    created_date: string
    modified_date: string
}

export interface OnCallBackups extends IdentifiableObject {
    backup_user_one: User | null
    backup_user_two: User | null
    backup_user_three: User | null
    created_date: string
    modified_date: string
}

export interface OnCallDay extends IdentifiableObject {
    user: User
    apartment: number
    start_time: string
    end_time: string
    on_call_date: string
    created_date: string
    modified_date: string
}

export enum CompanyStatus {
    ACTIVE = 'ACTIVE',
    IN_ACTIVE = 'INACTIVE',
    SUSPENDED = 'SUSPENDED',
    IN_HOUSE = 'IN_HOUSE',
    TEST = 'TEST',
    MANUAL_REMOVE_FROM_NVL = 'MANUAL_REMOVE_FROM_NVL',
}

export enum LeaseSyncSchedule {
    NONE = 'NONE',
    WEEKLY = 'WEEKLY',
    MONTHLY = 'MONTHLY',
}

export enum ApartmentVendorRelationshipStatus {
    NEGOTIATIONS = 'NEGOTIATIONS',
    ACTIVE = 'ACTIVE',
    ARCHIVED = 'ARCHIVED',
}

export interface ApartmentVendor extends IdentifiableObject {
    channel_id: number
    apartment: Company
    vendor: ListVendor
    vendor_see_unit_notes: boolean
    status: ApartmentVendorRelationshipStatus
    entrata_id: number | null
    entrata_gl_account_id: number | null
    entrata_location_id: string | null
    use_max_price: boolean
    professionalism?: number
    work_quality?: number
    rating_updated_at?: string | null
}

/** Nested read-only summary on company documents when typed. */
export interface OrganizationDocumentTypeSummary {
    id: number
    name: string
    is_active: boolean
    is_required_for_current_company?: boolean
    required_company_ids?: number[]
}

/** Organization-defined document type catalog (CRUD on organization_document_type). */
export interface OrganizationDocumentType extends IdentifiableObject {
    organization: number
    name: string
    description: string | null
    required_company_ids: number[]
    is_required_for_current_company: boolean
    is_active: boolean
    created_date: string
    modified_date: string
}

export interface OrganizationDocumentTypeCoverageDocument
    extends IdentifiableObject {
    name: string
    document: string
    created_date: string
    modified_date: string
}

export interface OrganizationDocumentTypeCoverageProperty
    extends IdentifiableNamedObject {
    is_required: boolean
    has_document: boolean
    documents: OrganizationDocumentTypeCoverageDocument[]
}

export interface OrganizationDocumentTypeCoverage {
    document_type: OrganizationDocumentTypeSummary & {
        description?: string | null
    }
    required_count: number
    required_missing_count: number
    uploaded_count: number
    missing_count: number
    properties: OrganizationDocumentTypeCoverageProperty[]
}

/** Organization-defined contract type catalog (CRUD on organization_contract_type). */
export interface OrganizationContractType extends IdentifiableObject {
    organization: number
    name: string
    description: string | null
    required_company_ids: number[]
    is_required_for_current_company: boolean
    is_active: boolean
    created_date: string
    modified_date: string
}

/** Nested read-only summary on contracts when typed. */
export interface OrganizationContractTypeSummary {
    id: number
    name: string
    is_active: boolean
    is_required_for_current_company?: boolean
    required_company_ids?: number[]
}

export type ContractLifecycleStatus =
    | 'active'
    | 'ending_soon'
    | 'auto_renew_notice'
    | 'expired'
    | 'no_dates'

export interface OrganizationContractTypeCoverageDocument
    extends IdentifiableObject {
    name: string
    document: string
    created_date: string
    modified_date: string
    end_date?: string | null
    lifecycle_status?: ContractLifecycleStatus
}

export interface OrganizationContractTypeCoverageProperty
    extends IdentifiableNamedObject {
    is_required: boolean
    has_document: boolean
    documents: OrganizationContractTypeCoverageDocument[]
}

export type OrganizationCertificateType = OrganizationContractType

export interface OrganizationContractTypeCoverage {
    contract_type: OrganizationContractTypeSummary & {
        description?: string | null
    }
    required_count: number
    required_missing_count: number
    uploaded_count: number
    missing_count: number
    properties: OrganizationContractTypeCoverageProperty[]
}

export interface CompanyDocumentSignableLink {
    id: number
    status: 'DRAFT' | 'READY' | 'ARCHIVED'
}

export interface CompanyDocument extends IdentifiableObject {
    name: string
    document: string
    ai_summary: string | null
    created_date: string
    modified_date: string
    /** Writable on create/update. Array of company IDs. */
    companies: number[]
    /** Read-only. Same as companies. */
    company_ids: number[]
    /** Read-only. Array of company names. */
    company_names: string[]
    /** FK to OrganizationDocumentType; null = general document. */
    document_type?: number | null
    document_type_detail?: OrganizationDocumentTypeSummary | null
    share_summary?: DocumentShareSummary | null
    /** Linked SignableDoc for the filtered property, when prepared for signing. */
    signable_doc?: CompanyDocumentSignableLink | null
}

export type DocumentShareMode =
    | 'none'
    | 'all_current'
    | 'all_current_and_future'

export interface DocumentShareSummary {
    share_mode: DocumentShareMode
    shared_tenant_count: number
    include_future_tenants: boolean
    label: string | null
}

export interface DocumentShareState {
    share_mode: DocumentShareMode
    tenant_ids: number[]
    shared_tenant_count: number
    include_future_tenants: boolean
}

export interface ShareableTenant {
    id: number
    name: string
    email: string
    unit_label?: string | null
    lease_status: number
}

export enum ContractPaymentTerm {
    MONTHLY = 'MONTHLY',
    QUARTERLY = 'QUARTERLY',
    ANNUALLY = 'ANNUALLY',
    ONE_TIME = 'ONE_TIME',
    PER_JOB = 'PER_JOB',
}

export enum ApartmentContractType {
    CERTIFICATE = 'CERTIFICATE',
    TURN = 'TURN',
    GENERAL_VENDOR = 'GENERAL_VENDOR',
}

export interface TurnContractDetails {
    turn_window?: string | null
    service_scope?: string[]
    trades_covered?: string[]
    pricing_structure?: string | null
    per_unit_price?: string | null
    per_bed_price?: string | null
    volume_assumptions?: string | null
    staffing_requirements?: string | null
    inspection_requirements?: string | null
    penalties?: string | null
    exclusions?: string[]
    important_deadlines?: string[]
}

export interface ApartmentContractAIExtractedData {
    summary?: string | null
    start_date?: string | null
    end_date?: string | null
    payment_amount?: string | null
    payment_term?: ContractPaymentTerm | string | null
    vendor_name?: string | null
    turn_details?: TurnContractDetails | null
    [key: string]: unknown
}

export interface ContractCertificate extends IdentifiableObject {
    contract: number
    file: string
    name: string
    expiration_date: string | null
    uploaded_by: number | null
    created_date: string
    modified_date: string
}

export interface ApartmentContract extends IdentifiableObject {
    name: string
    file: string
    ai_summary: string | null
    contract_type?: ApartmentContractType
    organization_contract_type?: number | null
    organization_contract_type_detail?: OrganizationContractTypeSummary | null
    organization_certificate_type?: number | null
    organization_certificate_type_detail?: OrganizationContractTypeSummary | null
    ai_extracted_data?: ApartmentContractAIExtractedData | null
    created_date: string
    modified_date: string
    /** Writable on create/update. Array of apartment IDs. */
    apartments: number[]
    /** Read-only. Same as apartments. */
    apartment_ids: number[]
    /** Read-only. Array of apartment names. */
    apartment_names: string[]
    vendor: ListVendor
    start_date: string | null
    end_date: string | null
    payment_amount: number | null
    payment_term: ContractPaymentTerm | null
    auto_renew: boolean
    renewal_term_months: number | null
    notice_lead_days: number | null
    lifecycle_status: ContractLifecycleStatus
    certificate_count: number
    certificates: ContractCertificate[]
}

export type LeaseTermSource = 'from_leases' | 'custom'

export interface ApartmentLeaseTerm {
    id: number
    name: string
    start_date: string
    end_date: string
    move_in_date: string
    move_out_date: string
    source: LeaseTermSource
}

export interface LeaseTermInput {
    name: string
    start_date: string
    end_date: string
    move_in_date: string
    move_out_date: string
}
